import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
} from '@backstage/core-components';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import {
  QueueOverviewItem,
  STATUS_COLORS,
  useQueuesOverview,
} from '../health/api';

const useStyles = makeStyles(theme => ({
  issue: { fontSize: 14, padding: theme.spacing(0.5, 0) },
  muted: { color: theme.palette.text.secondary },
  num: { fontVariantNumeric: 'tabular-nums' },
  state: { fontSize: 14, width: 24 },
}));

const fmt = (n: number | undefined) =>
  n === undefined ? '—' : n.toLocaleString('ru-RU');

const stateOf = (item: QueueOverviewItem) => {
  if (item.error) return { mark: '?', color: STATUS_COLORS.warning };
  if (item.issues.some(i => i.severity === 'critical'))
    return { mark: '✕', color: STATUS_COLORS.critical };
  if (item.issues.length) return { mark: '▲', color: STATUS_COLORS.warning };
  return { mark: '✓', color: STATUS_COLORS.ok };
};

const COLUMNS: Record<
  QueueOverviewItem['type'],
  { key: string; label: string }[]
> = {
  rabbitmq: [
    { key: 'ready', label: 'Готовых' },
    { key: 'unacked', label: 'Без ack' },
    { key: 'consumers', label: 'Консьюмеров' },
    { key: 'publishedPerSec', label: 'Публикаций/с' },
    { key: 'queues', label: 'Очередей' },
  ],
  bull: [
    { key: 'waiting', label: 'Ждут' },
    { key: 'active', label: 'В работе' },
    { key: 'delayed', label: 'Отложены' },
    { key: 'failedDelta1h', label: 'Упало за час' },
    { key: 'queues', label: 'Очередей' },
  ],
};

const Section = ({
  title,
  items,
}: {
  title: string;
  items: QueueOverviewItem[];
}) => {
  const classes = useStyles();
  if (!items.length) return null;
  const columns = COLUMNS[items[0].type];
  return (
    <Grid item xs={12}>
      <InfoCard title={title} noPadding>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.state} />
              <TableCell>Сущность</TableCell>
              {columns.map(c => (
                <TableCell key={c.key} align="right">
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(item => {
              const s = stateOf(item);
              return (
                <TableRow key={item.entity}>
                  <TableCell
                    className={classes.state}
                    style={{ color: s.color }}
                  >
                    {s.mark}
                  </TableCell>
                  <TableCell>
                    <EntityRefLink entityRef={item.entity} />
                    {item.error && (
                      <Typography variant="caption" className={classes.muted}>
                        {' '}
                        метрики недоступны
                      </Typography>
                    )}
                  </TableCell>
                  {columns.map(c => (
                    <TableCell
                      key={c.key}
                      align="right"
                      className={classes.num}
                    >
                      {fmt(item.totals?.[c.key])}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </InfoCard>
    </Grid>
  );
};

export const QueuesPage = () => {
  const classes = useStyles();
  const { value, error, loading } = useQueuesOverview();

  let content;
  if (loading) {
    content = <Progress />;
  } else if (!value) {
    content = (
      <Typography color="error">
        Не удалось получить очереди: {error?.message}
      </Typography>
    );
  } else {
    const issues = value.flatMap(item =>
      item.issues.map(issue => ({ item, issue })),
    );
    content = (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <InfoCard title="Проблемы">
            {issues.length ? (
              issues.map(({ item, issue }, n) => (
                <Typography
                  key={n}
                  className={classes.issue}
                  style={{ color: STATUS_COLORS[issue.severity] }}
                >
                  {issue.severity === 'critical' ? '✕' : '▲'}{' '}
                  <EntityRefLink entityRef={item.entity} />
                  {issue.queue ? ` · ${issue.queue}` : ''}: {issue.message}
                </Typography>
              ))
            ) : (
              <Typography className={classes.muted}>
                Проблем в очередях не видно
              </Typography>
            )}
          </InfoCard>
        </Grid>
        <Section
          title="RabbitMQ"
          items={value.filter(i => i.type === 'rabbitmq')}
        />
        <Section title="BullMQ" items={value.filter(i => i.type === 'bull')} />
      </Grid>
    );
  }

  return (
    <Page themeId="tool">
      <Header
        title="Очереди"
        subtitle="RabbitMQ и BullMQ по метрикам VictoriaMetrics, обновление раз в минуту"
      />
      <Content>{content}</Content>
    </Page>
  );
};
